// components/profile/sections/AboutYouSection.jsx
import React from 'react';
import { FormField } from '../components/FormField';
import { ImageUpload } from '../components/ImageUpload';
import { useProfileFormContext } from '../context/ProfileFormContext';
import { 
  UserCircleIcon,
  SparklesIcon,
  CameraIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

export const AboutYouSection = () => {
  const { 
    formData, 
    updateField, 
    setFieldTouched, 
    errors, 
    touched 
  } = useProfileFormContext();

  return (
    <div className="card" style={{
      backgroundColor: 'var(--Surface-Primary)',
      borderRadius: 'var(--radius-xl)',
      padding: 'var(--spacing-l)',
      boxShadow: 'var(--shadow-frame-small)'
    }}>
      {/* Section Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 flex items-center justify-center" style={{
            background: 'var(--brand-gradient-2)',
            borderRadius: 'var(--radius-s)'
          }}>
            <UserCircleIcon className="w-5 h-5" style={{ color: 'var(--Grey-800)' }} />
          </div>
          <h2 style={{
            fontSize: 'var(--font-size-h5)',
            fontWeight: 'var(--font-weight-h5)',
            color: 'var(--Text-Primary)',
            lineHeight: 'var(--line-height-h5)'
          }}>
            About You
          </h2>
        </div>
        <p style={{ 
          color: 'var(--Text-Secondary)',
          fontSize: 'var(--font-size-body3)',
          marginLeft: '52px'
        }}>
          People want to connect with the person behind the brand. Use this space to tell your story, share your inspiration, and let customers see the real person who brings each piece to life.
        </p>
      </div>

      {/* NEW: Header Image Section - ADDED TO ABOUT TAB */}
      <div className="mb-8" style={{
        backgroundColor: 'var(--Surface-Secondary)',
        borderRadius: 'var(--radius-l)',
        padding: 'var(--spacing-l)',
        border: '1px solid var(--Stroke-Tertiary)',
        textAlign: 'center'
      }}>
        <FormField
          label="Header Image"
          required
          error={touched.headerImage && errors.headerImage}
          helperText="A banner image for your profile (3:1 ratio, e.g. 1200x600px)"
          tooltipText="This header image will appear at the top of your profile page. Choose an image that represents your brand or showcases your work."
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-h6)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)',
            textAlign: 'center',
            display: 'block',
            width: '100%',
            marginBottom: 'var(--spacing-s)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
            <ImageUpload
              value={formData.headerImage}
              onChange={(value) => {
                updateField('headerImage', value);
                setFieldTouched('headerImage');
              }}
              label="Upload Header Image"
              aspectRatio="header"
              maxSize={5}
            />
          </div>
        </FormField>
      </div>
      {/* END NEW SECTION */}
      
      {/* Image Upload Cards - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Profile Photo Card */}
        <div style={{
          backgroundColor: 'var(--Surface-Secondary)',
          borderRadius: 'var(--radius-l)',
          padding: 'var(--spacing-l)',
          border: '1px solid var(--Stroke-Tertiary)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <FormField
            label="Profile Photo"
            required
            error={touched.profilePhoto && errors.profilePhoto}
            helperText="A professional photo of yourself"
            tooltipText="Customers connect better when they can see who they're working with"
            hideCompletionIcon={true}
            labelStyle={{
              fontSize: 'var(--font-size-h6)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--Text-Primary)',
              textAlign: 'center',
              display: 'block',
              width: '100%'
            }}
          >
            <ImageUpload
              value={formData.profilePhoto}
              onChange={(value) => {
                updateField('profilePhoto', value);
                setFieldTouched('profilePhoto');
              }}
              label="Upload Photo"
              aspectRatio="square"
              maxSize={5}
            />
          </FormField>
        </div>

        {/* Brand Icon Card */}
        <div style={{
          backgroundColor: 'var(--Surface-Secondary)',
          borderRadius: 'var(--radius-l)',
          padding: 'var(--spacing-l)',
          border: '1px solid var(--Stroke-Tertiary)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <FormField
            label="Brand Icon"
            required
            error={touched.brandIcon && errors.brandIcon}
            helperText="Your brand logo or icon"
            tooltipText="This will appear on your storefront and profile"
            hideCompletionIcon={true}
            labelStyle={{
              fontSize: 'var(--font-size-h6)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--Text-Primary)',
              textAlign: 'center',
              display: 'block',
              width: '100%'
            }}
          >
            <ImageUpload
              value={formData.brandIcon}
              onChange={(value) => {
                updateField('brandIcon', value);
                setFieldTouched('brandIcon');
              }}
              label="Upload Icon"
              aspectRatio="square"
              maxSize={5}
            />
          </FormField>
        </div>
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <FormField
          label="First Name"
          required
          error={touched.firstName && errors.firstName}
          helperText="Your first name"
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <input
            type="text"
            value={formData.firstName || ''}
            onChange={(e) => updateField('firstName', e.target.value)}
            onBlur={() => setFieldTouched('firstName')}
            className="form-input"
            placeholder="John"
            style={{
              width: '100%',
              padding: 'var(--spacing-s)',
              borderRadius: 'var(--radius-s)',
              border: '1px solid var(--Stroke-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          />
        </FormField>

        {/* Surname */}
        <FormField
          label="Surname"
          required
          error={touched.surname && errors.surname}
          helperText="Your last name"
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <input
            type="text"
            value={formData.surname || ''}
            onChange={(e) => updateField('surname', e.target.value)}
            onBlur={() => setFieldTouched('surname')}
            className="form-input"
            placeholder="Smith"
            style={{
              width: '100%',
              padding: 'var(--spacing-s)',
              borderRadius: 'var(--radius-s)',
              border: '1px solid var(--Stroke-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          />
        </FormField>

        {/* Professional Title */}
        <div className="md:col-span-2">
          <FormField
            label="Professional Title"
            required
            error={touched.title && errors.title}
            helperText="Your role in the business"
            tooltipText="e.g., Master Jeweller, Designer & Maker, Founder, Creative Director"
            hideCompletionIcon={true}
            labelStyle={{
              fontSize: 'var(--font-size-subtitle3)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--Text-Primary)'
            }}
          >
            <input
              type="text"
              value={formData.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
              onBlur={() => setFieldTouched('title')}
              className="form-input"
              placeholder="e.g., Founder & Master Jeweller"
              style={{
                width: '100%',
                padding: 'var(--spacing-s)',
                borderRadius: 'var(--radius-s)',
                border: '1px solid var(--Stroke-Secondary)',
                fontSize: 'var(--font-size-body3)'
              }}
            />
          </FormField>
        </div>

        {/* Bio */}
        <div className="md:col-span-2">
          <FormField
            label="Your Story"
            required
            error={touched.bio && errors.bio}
            helperText={`Share your background and passion for jewellery (${formData.bio?.length || 0}/1000 characters)`}
            tooltipText="This is your chance to connect with customers on a personal level. Share your journey, what inspires you, and what makes your approach to jewellery unique. A compelling story can turn visitors into customers."
            hideCompletionIcon={true}
            labelStyle={{
              fontSize: 'var(--font-size-subtitle3)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--Text-Primary)'
            }}
          >
            <textarea
              value={formData.bio || ''}
              onChange={(e) => updateField('bio', e.target.value)}
              onBlur={() => setFieldTouched('bio')}
              className="form-textarea"
              rows={6}
              maxLength={1000}
              placeholder="Tell your story... What inspired you to become a jeweller? What's your design philosophy? What makes your approach unique?"
              style={{
                width: '100%',
                padding: 'var(--spacing-s)',
                borderRadius: 'var(--radius-s)',
                border: '1px solid var(--Stroke-Secondary)',
                fontSize: 'var(--font-size-body3)',
                resize: 'vertical',
                minHeight: '150px'
              }}
            />
            {/* Character count progress bar */}
            <div className="mt-2 h-1 overflow-hidden" style={{
              backgroundColor: 'var(--Grey-200)',
              borderRadius: 'var(--radius-pill)'
            }}>
              <div 
                className="h-full transition-all duration-300"
                style={{ 
                  background: 'var(--brand-gradient-1)',
                  width: `${(formData.bio?.length || 0) / 10}%`,
                  borderRadius: 'var(--radius-pill)'
                }}
              />
            </div>
          </FormField>
        </div>
      </div>

      {/* Combined Tips Section */}
      <div className="mt-8" style={{
        background: 'var(--brand-gradient-2)',
        borderRadius: 'var(--radius-m)',
        padding: 'var(--spacing-m)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-s)'
      }}>
        <span style={{ fontSize: '25px', marginTop: '0px' }}>💡</span>
        <div>
          <h6 style={{
            fontSize: 'var(--font-size-h6)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--Text-Primary)',
            marginBottom: 'var(--spacing-xxs)',
            margin: 0
          }}>
            Tips for this section
          </h6>
          <p style={{
            fontSize: 'var(--font-size-body4)',
            color: 'var(--Grey-900)',
            margin: 0,
            marginTop: 'var(--spacing-xxs)',
            lineHeight: '2'
          }}>
            • Your header image creates the first impression - choose something that showcases your craftsmanship.
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Profile photos that show your face build trust with customers.
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Your brand icon should be recognisable even at small sizes.
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Share your unique journey - what brought you to jewellery making?
          </p>
        </div>
      </div>
    </div>
  );
};